import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Camera, Check, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Iter-112 · Guide Capture Floater
 * A small floating camera button (bottom-left) that captures the current
 * Zenith 360 page via html2canvas and uploads it as the illustration for
 * a feature the user picks (auto-suggested from the URL match).
 *
 * Visible only for admin / owner / super-admin roles, and only on `/app/*`.
 */
export default function GuideCaptureFloater() {
  const loc = useLocation();
  const { user } = useAuth() || {};
  const [features, setFeatures] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);

  const role = (user?.role || "").toLowerCase();
  const isAllowed = !!user && ["admin", "owner", "super_admin", "business_owner", "accountant", "cfo"].includes(role);
  const inAppRoute = loc.pathname.startsWith("/app/");
  const hideOnGuide = loc.pathname.startsWith("/app/user-guide");

  useEffect(() => {
    if (!isAllowed || !inAppRoute) return;
    api.get("/user-guide")
       .then((r) => {
         const flat = (r.data?.sections || []).flatMap((s) =>
           s.features.map((f) => ({ ...f, section: s.title })));
         setFeatures(flat);
       })
       .catch(() => setFeatures([]));
  }, [isAllowed, inAppRoute]);

  // Auto-suggest the feature whose `where` matches the current URL best
  const suggested = useMemo(() => {
    if (!features.length) return null;
    const path = loc.pathname + loc.search;
    // Longest prefix match on `where`
    let best = null; let bestLen = -1;
    for (const f of features) {
      const w = f.where || "";
      if (path === w || path.startsWith(w)) {
        if (w.length > bestLen) { best = f; bestLen = w.length; }
      }
    }
    return best;
  }, [features, loc]);

  useEffect(() => { if (suggested) setSelected(suggested.code); }, [suggested]);

  const capture = async () => {
    if (!selected) { toast.error("Pick a feature first"); return; }
    setBusy(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      // Hide the floater panel first
      const panel = document.getElementById("guide-capture-floater");
      const prevVis = panel?.style.visibility;
      if (panel) panel.style.visibility = "hidden";
      const canvas = await html2canvas(document.body, {
        useCORS: true, backgroundColor: "#ffffff", scale: 1.5,
        windowWidth: document.documentElement.scrollWidth,
        height: Math.min(document.documentElement.scrollHeight, 1400),
      });
      if (panel) panel.style.visibility = prevVis || "";
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.92));
      if (!blob) throw new Error("Canvas → blob failed");
      const fd = new FormData();
      fd.append("file", blob, `${selected}.png`);
      await api.post(`/user-guide/upload-screenshot/${selected}`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`✓ Captured for "${features.find((f) => f.code === selected)?.name || selected}"`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Capture failed");
    }
    setBusy(false);
  };

  if (!isAllowed || !inAppRoute || hideOnGuide) return null;

  return (
    <div id="guide-capture-floater"
           className="fixed bottom-24 left-6 z-[9998]"
           data-testid="guide-capture-floater">
      {!open ? (
        <button onClick={() => setOpen(true)}
                  title="Capture this page for the User Guide"
                  className="w-11 h-11 rounded-full bg-accent hover:bg-accent/90 text-white shadow-xl flex items-center justify-center transition-transform hover:scale-105"
                  data-testid="guide-capture-btn">
          <Camera className="w-5 h-5" />
        </button>
      ) : (
        <div className="w-[320px] rounded-sm border-2 border-accent bg-card shadow-2xl overflow-hidden">
          <div className="bg-accent text-white px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Camera className="w-3.5 h-3.5" />
              <span className="text-xs font-heading font-semibold">Capture for User Guide</span>
            </div>
            <button onClick={() => setOpen(false)} className="hover:opacity-70" data-testid="guide-capture-close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-3 space-y-2.5">
            <div className="text-[10px] font-mono uppercase text-muted-foreground">
              // Pick the feature this page documents
              {suggested && <span className="text-emerald-700"> · auto-suggested</span>}
            </div>
            <select value={selected} onChange={(e) => setSelected(e.target.value)}
                    className="w-full h-8 rounded-sm border border-border bg-background text-sm px-2"
                    data-testid="guide-capture-select">
              <option value="">— Select feature —</option>
              {features.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.section} › {f.name}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-muted-foreground leading-tight">
              This will capture the visible viewport of this page (html2canvas) and
              replace the guide illustration for the selected feature. Real Zenith 360
              pixels, no AI.
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setOpen(false)}
                      className="flex-1 h-8 rounded-sm border border-border text-xs hover:bg-muted"
                      data-testid="guide-capture-cancel">
                Cancel
              </button>
              <button onClick={capture} disabled={busy || !selected}
                      className="flex-1 h-8 rounded-sm bg-accent hover:bg-accent/90 text-white text-xs flex items-center justify-center gap-1 disabled:opacity-50"
                      data-testid="guide-capture-save">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {busy ? "Capturing…" : "Capture & save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
